import React, { useState, useEffect } from 'react';
// GenericFields component manages a dynamic list of fields with options for 
// adding, removing, and editing field properties (key, value, type, default)
//  in either 'header' or 'fields' mode, and updates the parent component with the changes.

interface Field {
  key: string;
  value: string;
  type?: string;
  default?: string;
}

interface GenericFieldsProps {
  value: string;
  onChange: (fields: string) => void;
  mode?: 'header' | 'fields';
}

const GenericFields: React.FC<GenericFieldsProps> = ({ value, onChange, mode = 'fields' }) => {
  const parseFields = (fieldsString: string): Field[] => {
    if (!fieldsString) {
      return mode === 'header' 
        ? [{ key: '', value: '' }]
        : [{ key: '', value: 'Text', default: '' }];
    }

    if (mode === 'header') {

      return fieldsString.split(',').map((field) => {
        const [key, value] = field.split(':');
        return {
          key: key?.trim() || '',
          value: value?.trim() || ''
        };
      });
    } else {

      return fieldsString.split(',').map((field) => {
        const [key, type, defaultValue] = field.split(':');
        return {
          key: key?.trim() || '',
          value: type?.trim() || 'Text',
          default: defaultValue?.trim() || ''
        };
      });
    }
  };

  const [fields, setFields] = useState<Field[]>(parseFields(value));

  useEffect(() => {
    setFields(parseFields(value));
  }, [value]);

  const handleAddField = () => {
    const newField = mode === 'header'
      ? { key: '', value: '' }
      : { key: '', value: 'Text', default: '' };
    
    const updatedFields = [...fields, newField];
    setFields(updatedFields);
    updateParent(updatedFields);
  };

  const handleRemoveField = (index: number) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);
    updateParent(updatedFields);
  };

  const handleFieldChange = (index: number, fieldKey: keyof Field, newValue: string) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, [fieldKey]: newValue } : field
    );
    setFields(updatedFields);
    updateParent(updatedFields);
  };

  const updateParent = (updatedFields: Field[]) => {
    const validFields = updatedFields.filter((field) => field.key.trim() !== '');
    
    if (mode === 'header') {

      const headerObject = validFields.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {} as Record<string, string>);
      

      const headerString = Object.entries(headerObject)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');
      
      onChange(headerString);
    } else {

      const fieldsString = validFields
        .map((field) => `${field.key}:${field.value}:${field.default || ''}`)
        .join(',');
      
      onChange(fieldsString);
    }
  };

  return (
    <div className="space-y-2">
      {fields.map((field, index) => (
        <div key={index} className="d-flex align-items-center mb-2">
          <input
            type="text"
            className="form-control me-2"
            placeholder={mode === 'header' ? 'Key' : 'Field Name'}
            value={field.key}
            onChange={(e) => handleFieldChange(index, 'key', e.target.value)}
          />

          {mode === 'header' ? (
            <input
              type="text"
              className="form-control me-2"
              placeholder="Value"
              value={field.value}
              onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
            />
          ) : (
            <>
              <select
                className="form-select me-2"
                value={field.value}
                onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
              >
                <option value="Text">Text</option>
                <option value="Number">Number</option>
                <option value="Boolean">Boolean</option>
              </select>
              <input
                type="text"
                className="form-control me-2"
                placeholder="Default"
                value={field.default || ''}
                onChange={(e) => handleFieldChange(index, 'default', e.target.value)}
              />
            </>
          )}

          <button
            type="button"
            onClick={handleAddField}
            className="btn btn-primary me-2"
          >
            +
          </button>
          {fields.length > 1 && (
            <button
              type="button"
              onClick={() => handleRemoveField(index)}
              className="btn btn-danger"
            >
              -
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default GenericFields;